import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { XMarkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import moment from 'moment/moment';
import { toast } from 'react-toastify';
import { toastConfiguration } from '../Constant/Data';
import { useReduxStore } from '../hooks/useReduxStore';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function ApiUsageModal({ isOpen, onClose, data }) {
  const {getState}=useReduxStore()
  const {userData}=getState("AUTH")
  if (!isOpen || !data) return null;
// console.log("data-----------",data)
  // Format the API key for display (show first 8 and last 8 characters)
  const formatApiKey = (key) => {
    if (!key) return 'N/A';
    if (key.length <= 16) return key;
    return `${key.substring(0, 8)}...${key.substring(key.length - 8)}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Copy API key to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.info("API key copied to clipboard",{...toastConfiguration,hideProgressBar:true,autoClose:1000,newestOnTop:true});
      
      // You can add a toast notification here if needed
      console.log('API key copied to clipboard');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Dummy chart data (you can replace this with real usage data later)
  const usageData = {
    labels: ['Jun 1', 'Jun 5', 'Jun 10', 'Jun 15', 'Jun 20', 'Jun 25'],
    datasets: [
      {
        label: 'API Requests',
        data: [100, 180, 130, 90, 160, 240],
        backgroundColor: '#3662E3',
        borderRadius: 6,
        barThickness: 28,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#fff',
        bodyColor: '#fff',
        cornerRadius: 4,
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#9ca3af', font: { size: 12 } },
      },
      y: {
        grid: { display: false },
        ticks: { color: '#9ca3af', font: { size: 12 } },
      },
    },
  };
const getTotalDays=()=>{
  const startDate = new Date(data?.createdAt);
  const endDate = new Date();
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  return totalDays;
}
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
      <div className="w-full sm:w-[600px] bg-blueGray border-l border-borderGray h-full max-h-screen shadow-xl p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Key Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Key Details */}
        <div className="bg-[#384355] p-4 rounded-lg border border-borderGray mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Key Information</h3>
          <div className="text-sm text-gray-300 space-y-3">
            <p><span className="font-medium text-white">API Key Name:</span> <span className="text-white">{data?.label || 'N/A'}</span></p>
            <p><span className="font-medium text-white">Key ID:</span> <span className="font-mono text-gray-300">{data?.id || data?._id || 'N/A'}</span></p>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-white">API Key:</span>
              <div className="flex items-center space-x-2 bg-gray-800 px-3 py-1 rounded">
                <span className="font-mono text-gray-300 text-xs">{formatApiKey(data?.key)}</span>
                <button
                  onClick={() => copyToClipboard(data?.key)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Copy full API key"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p><span className="font-medium text-white">Created On:</span> <span className="text-gray-300">{formatDate(data?.createdAt)}</span></p>
            <p><span className="font-medium text-white">Last Updated:</span> <span className="text-gray-300">{formatDate(data?.updatedAt)}</span></p>
            <p><span className="font-medium text-white">Status:</span>
            {data?.isActive ? <span className="text-green-400 font-semibold"> Active</span> :
            <span className="text-red-400 font-semibold"> Disabled</span>
            }
             </p>
            <p><span className="font-medium text-white">User:</span> <span className="text-primaryBlue">{userData?.name || 'N/A'}</span></p>
            <p><span className="font-medium text-white">Email:</span> <span className="text-gray-300">{userData?.email || 'N/A'}</span></p>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="bg-[#384355] p-4 rounded-lg border border-borderGray mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Usage Stats</h2>
          <div className="text-sm text-gray-300 space-y-3">
            <p><span className="font-medium text-white">This Month:</span> <span className="text-primaryBlue">{data?.count} requests</span></p>
            <p><span className="font-medium text-white">Daily Avg:</span> <span className="text-primaryBlue">{data?.count/getTotalDays()} requests</span></p>
            <p><span className="font-medium text-white">Last Used:</span> <span className="text-gray-300">{formatDate(data?.lastUsed||data?.updatedAt)}</span></p>
            {/* <p><span className="font-medium text-white">Top Endpoint:</span> <span className="font-mono text-primaryBlue">/v1/radar/forecast</span></p> */}
          </div>
        </div>

        {/* Bar Chart */}
        {/* <div className="bg-[#384355] p-4 rounded-lg border border-borderGray">
          <h3 className="text-lg font-semibold text-white mb-4">Usage Chart</h3>
          <div className="w-full bg-[#212c43] p-4 rounded-lg border border-borderGray">
            <Bar data={usageData} options={chartOptions} />
          </div>
        </div> */}
      </div>
    </div>
  );
}
